import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "./usePageSEO";

type Lang = "en" | "ar";

interface BaseSEO {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  robots?: string;
  jsonLd?: Record<string, any>;
  hreflang?: { lang: string; href: string }[];
  noindex?: boolean;
}

/**
 * Like usePageSEO, but first looks up the current path in the `pages` table
 * and merges any non-empty SEO/OG fields on top of the supplied defaults.
 * Editors can override title, description, OG fields, canonical and image
 * directly from /admin/pages without touching code.
 */
export function useCmsPageSEO(defaults: BaseSEO, pathOverride?: string) {
  const { lang } = useLanguage();
  const language = (lang === "ar" ? "ar" : "en") as Lang;
  const [overrides, setOverrides] = useState<Partial<BaseSEO>>({});

  // Resolve current path (strip /ar prefix and trailing slashes)
  const path = (() => {
    if (pathOverride) return normalizePath(pathOverride);
    if (typeof window === "undefined") return "/";
    return normalizePath(window.location.pathname);
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("pages")
        .select(
          "seo_title_en,seo_title_ar,seo_description_en,seo_description_ar,og_title_en,og_title_ar,og_description_en,og_description_ar,og_image,canonical_url,title_en,title_ar"
        )
        .eq("path", path)
        .eq("status", "published")
        .maybeSingle();
      if (cancelled || !data) return;

      const pick = (en?: string | null, ar?: string | null) => {
        const v = language === "ar" ? ar || en : en;
        return v && v.trim().length > 0 ? v : undefined;
      };

      const next: Partial<BaseSEO> = {};
      const seoTitle = pick(data.seo_title_en, data.seo_title_ar);
      const titleFallback = pick(data.title_en, data.title_ar);
      if (seoTitle || titleFallback) next.title = seoTitle || titleFallback!;
      const seoDesc = pick(data.seo_description_en, data.seo_description_ar);
      if (seoDesc) next.description = seoDesc;
      const ogT = pick(data.og_title_en, data.og_title_ar);
      if (ogT) next.ogTitle = ogT;
      const ogD = pick(data.og_description_en, data.og_description_ar);
      if (ogD) next.ogDescription = ogD;
      if (data.og_image) next.ogImage = data.og_image;
      if (data.canonical_url) next.canonical = data.canonical_url;

      setOverrides(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [path, language]);

  usePageSEO({ ...defaults, ...overrides });
}

function normalizePath(p: string) {
  let out = p || "/";
  if (out.startsWith("/ar/")) out = out.slice(3);
  else if (out === "/ar") out = "/";
  if (out.length > 1 && out.endsWith("/")) out = out.slice(0, -1);
  return out;
}